import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { toast } from "sonner";
import { Shield, Key, X, Copy, CheckCircle2 } from "lucide-react";

/**
 * Security section for MiPerfil:
 *  - Change Password (any password user)
 *  - Enable/Disable 2FA (admin only, only for password accounts)
 */
export default function SecuritySection() {
  const { user, checkAuth } = useAuth();
  const { t } = useI18n();
  const [openChangePw, setOpenChangePw] = useState(false);
  const [openSetup2FA, setOpenSetup2FA] = useState(false);
  const [openDisable2FA, setOpenDisable2FA] = useState(false);

  const isGoogle = user?.auth_type === "google";
  const can2FA = !isGoogle && user?.is_admin;
  const totpOn = !!user?.totp_enabled;

  return (
    <section className="bg-zinc-900 border border-zinc-800 p-6 md:p-8" data-testid="security-section">
      <div className="flex items-center gap-3 mb-4">
        <Shield size={20} className="text-[var(--c-lime)]" />
        <h2 className="font-display text-2xl md:text-3xl uppercase leading-none">{t("security.section.title")}</h2>
      </div>
      <p className="text-zinc-400 text-sm mb-6">{t("security.section.subtitle")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Change password card */}
        <div className="border border-zinc-800 p-5 flex flex-col" data-testid="security-card-password">
          <div className="flex items-center gap-2 mb-2">
            <Key size={16} className="text-[var(--c-peach)]" />
            <div className="text-[11px] uppercase tracking-widest font-mono-data">{t("security.changePassword.title")}</div>
          </div>
          <div className="text-xs text-zinc-500 mb-4 flex-1">
            {isGoogle ? t("security.changePassword.googleOnly") : " "}
          </div>
          <button
            data-testid="open-change-password"
            disabled={isGoogle}
            onClick={() => setOpenChangePw(true)}
            className="self-start bg-[var(--c-peach)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("security.changePassword.cta")}
          </button>
        </div>

        {/* 2FA card (admin only, password accounts only) */}
        {can2FA && (
          <div className="border border-zinc-800 p-5 flex flex-col" data-testid="security-card-2fa">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} className="text-[var(--c-lime)]" />
              <div className="text-[11px] uppercase tracking-widest font-mono-data">{t("security.twoFactor.title")}</div>
            </div>
            <div className="text-xs text-zinc-500 mb-2 flex-1">{t("security.twoFactor.subtitle")}</div>
            <div className="text-xs mb-4">
              <span className="text-zinc-400">Status: </span>
              <span
                data-testid="twofa-status"
                className={totpOn ? "text-[var(--c-lime)] font-bold" : "text-zinc-300"}
              >
                {totpOn ? t("security.twoFactor.status.on") : t("security.twoFactor.status.off")}
              </span>
            </div>
            {totpOn ? (
              <button
                data-testid="open-disable-2fa"
                onClick={() => setOpenDisable2FA(true)}
                className="self-start border border-zinc-700 text-zinc-200 hover:border-white font-bold uppercase tracking-wider px-4 py-2 text-xs"
              >
                {t("security.twoFactor.disable")}
              </button>
            ) : (
              <button
                data-testid="open-setup-2fa"
                onClick={() => setOpenSetup2FA(true)}
                className="self-start bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs"
              >
                {t("security.twoFactor.enable")}
              </button>
            )}
          </div>
        )}
      </div>

      {openChangePw && (
        <ChangePasswordModal
          onClose={() => setOpenChangePw(false)}
        />
      )}
      {openSetup2FA && (
        <Setup2FAModal
          onClose={async () => {
            setOpenSetup2FA(false);
            await checkAuth();
          }}
        />
      )}
      {openDisable2FA && (
        <Disable2FAModal
          onClose={async () => {
            setOpenDisable2FA(false);
            await checkAuth();
          }}
        />
      )}
    </section>
  );
}

/* ============================================================ */
/*  Change Password Modal                                       */
/* ============================================================ */
function ChangePasswordModal({ onClose }) {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error(t("security.changePassword.tooShort"));
      return;
    }
    if (next !== confirm) {
      toast.error(t("security.changePassword.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("security.changePassword.success"));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err.save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={t("security.changePassword.title")} onClose={onClose} testid="change-password-modal">
      <form onSubmit={submit} className="space-y-3">
        <input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder={t("security.changePassword.current")}
          data-testid="cp-current"
          className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2.5 text-sm rounded-sm"
          required
        />
        <input
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={t("security.changePassword.new")}
          data-testid="cp-new"
          className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2.5 text-sm rounded-sm"
          required
          minLength={8}
        />
        <input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={t("security.changePassword.confirm")}
          data-testid="cp-confirm"
          className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2.5 text-sm rounded-sm"
          required
          minLength={8}
        />
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-wider"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="cp-submit"
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("security.changePassword.submit")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/* ============================================================ */
/*  Setup 2FA Modal (QR + verify -> recovery codes)             */
/* ============================================================ */
function Setup2FAModal({ onClose }) {
  const { t } = useI18n();
  const [stage, setStage] = useState("loading"); // loading | scan | recovery
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    api.post("/auth/2fa/setup", {})
      .then((r) => {
        if (cancelled) return;
        setQr(r.data.qr);
        setSecret(r.data.secret);
        setStage("scan");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || t("err.save"));
        onClose();
      });
    return () => { cancelled = true; };
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/enable", { code: code.trim() });
      setRecoveryCodes(r.data.recovery_codes || []);
      setStage("recovery");
      toast.success(t("security.twoFactor.setup.success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err.save"));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard error");
    }
  };

  return (
    <ModalShell title={t("security.twoFactor.title")} onClose={onClose} testid="setup-2fa-modal">
      {stage === "loading" && (
        <div className="text-sm text-zinc-400 py-8 text-center">{t("common.loading")}</div>
      )}
      {stage === "scan" && (
        <form onSubmit={verify} className="space-y-4">
          <p className="text-xs text-zinc-400">{t("security.twoFactor.setup.step1")}</p>
          <div className="flex justify-center">
            {qr && (
              <img
                src={qr}
                alt="QR 2FA"
                data-testid="twofa-qr"
                className="w-48 h-48 bg-white p-2"
              />
            )}
          </div>
          <div className="text-[11px] text-zinc-500 space-y-1">
            <div>{t("security.twoFactor.setup.secretLabel")}</div>
            <div
              data-testid="twofa-secret"
              className="font-mono text-[11px] text-zinc-300 break-all bg-black border border-zinc-800 px-3 py-2 select-all"
            >
              {secret}
            </div>
          </div>
          <p className="text-xs text-zinc-400">{t("security.twoFactor.setup.step2")}</p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("security.twoFactor.setup.codeInput")}
            data-testid="twofa-setup-code"
            className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2.5 text-center tracking-widest font-mono-data text-lg rounded-sm"
            maxLength={20}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-wider"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="twofa-setup-verify"
              className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50"
            >
              {busy ? t("common.saving") : t("security.twoFactor.setup.verify")}
            </button>
          </div>
        </form>
      )}
      {stage === "recovery" && (
        <div className="space-y-4" data-testid="twofa-recovery-stage">
          <div className="flex items-center gap-2 text-[var(--c-lime)]">
            <CheckCircle2 size={18} />
            <span className="text-sm font-bold uppercase tracking-wider">
              {t("security.twoFactor.recovery.title")}
            </span>
          </div>
          <p className="text-xs text-zinc-400">{t("security.twoFactor.recovery.intro")}</p>
          <div
            className="grid grid-cols-2 gap-2 bg-black border border-[var(--c-lime)] p-3"
            data-testid="twofa-recovery-codes"
          >
            {recoveryCodes.map((c) => (
              <div key={c} className="font-mono text-sm text-[var(--c-lime)] tracking-widest text-center select-all py-1">
                {c}
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={copyCodes}
              data-testid="twofa-recovery-copy"
              className="border border-zinc-700 hover:border-white px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-1"
            >
              <Copy size={12} />
              {copied ? t("security.twoFactor.recovery.copied") : t("security.twoFactor.recovery.copy")}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="twofa-recovery-done"
              className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs"
            >
              {t("security.twoFactor.recovery.done")}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/* ============================================================ */
/*  Disable 2FA Modal                                            */
/* ============================================================ */
function Disable2FAModal({ onClose }) {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { current_password: pw, code: code.trim() });
      toast.success(t("security.twoFactor.disable.success"));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err.save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={t("security.twoFactor.disable.title")} onClose={onClose} testid="disable-2fa-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-zinc-400">{t("security.twoFactor.disable.intro")}</p>
        <input
          type="password"
          autoComplete="current-password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder={t("security.twoFactor.disable.password")}
          data-testid="twofa-disable-password"
          className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2.5 text-sm rounded-sm"
          required
        />
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t("security.twoFactor.disable.code")}
          data-testid="twofa-disable-code"
          className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2.5 text-sm rounded-sm font-mono-data tracking-widest"
          maxLength={20}
          required
        />
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-wider"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={busy || !pw || !code.trim()}
            data-testid="twofa-disable-submit"
            className="bg-red-600 text-white font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("security.twoFactor.disable.submit")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/* ============================================================ */
/*  Modal shell                                                  */
/* ============================================================ */
function ModalShell({ title, onClose, testid, children }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid={testid}
    >
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-md p-6 space-y-4 my-8 md:my-0 relative z-[61]">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl md:text-2xl uppercase">{title}</h3>
          <button
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="p-2 border border-zinc-800 hover:border-white"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
